import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { ChevronDown, ChevronRight, Inbox, RefreshCw, Search, SearchX, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/shadcn/empty";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/shadcn/input-group";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Spinner } from "@/components/shadcn/spinner";
import { MailDirectionToggle } from "./MailDirectionToggle";
import { MailListItem } from "./MailListItem";
import { MailListViewToggle } from "./MailListViewToggle";
import { MailTaskFilterMenu } from "./MailTaskFilterMenu";
import { groupByDate } from "../mailFormat";
import { taskFilterOption } from "../mailTaskStatus";
import type { MailDirectionFilter, MailListView, MailSummary, MailTaskFilter } from "../types";

type MailListNavigation = "previous" | "next" | "first" | "last";

const NAVIGATION_KEYS: Record<string, MailListNavigation> = {
  ArrowUp: "previous",
  ArrowDown: "next",
  Home: "first",
  End: "last",
};

type MailListPaneProps = {
  items: MailSummary[];
  /** The list row to show as selected: the email itself, or the row of its conversation. */
  selectedMailId: string | null;
  searchText: string;
  activeSearch: string;
  view: MailListView;
  direction: MailDirectionFilter;
  taskFilter: MailTaskFilter;
  /** Show which customers each message is filed against, for lists that span customers. */
  showCustomers: boolean;
  /** What to say when there is no mail at all, before any search or filter. */
  emptyDescription: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  onSearchTextChange: (value: string) => void;
  onViewChange: (value: MailListView) => void;
  onDirectionChange: (value: MailDirectionFilter) => void;
  onTaskFilterChange: (value: MailTaskFilter) => void;
  onSelect: (mailId: string) => void;
  /** Selects an email moved to with the keyboard, without leaving the list on small screens. */
  onNavigate: (mailId: string) => void;
  onLoadMore: () => void;
  onRetry: () => void;
};

const ListSkeleton = () => (
  <div className="space-y-4 p-3" aria-hidden>
    {Array.from({ length: 6 }, (_, index) => (
      <div key={index} className="space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    ))}
  </div>
);

type ListEmptyProps = {
  activeSearch: string;
  direction: MailDirectionFilter;
  taskFilter: MailTaskFilter;
  emptyDescription: string;
};

const ListEmpty = ({ activeSearch, direction, taskFilter, emptyDescription }: ListEmptyProps) => {
  const isFiltered = Boolean(activeSearch) || direction !== "all" || taskFilter !== "all";

  const description = activeSearch
    ? `Nothing matches "${activeSearch}".`
    : taskFilter !== "all"
      ? taskFilterOption(taskFilter).emptyDescription
      : direction === "inbound"
      ? "There are no received emails."
      : direction === "outbound"
        ? "There are no sent emails."
        : emptyDescription;

  return (
    <Empty className="py-12">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="bg-kPurple-50 text-kPurple-500">{isFiltered ? <SearchX /> : <Inbox />}</EmptyMedia>
        <EmptyTitle>{isFiltered ? "No matching emails" : "No emails yet"}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
};

type ActiveTaskFilterProps = {
  taskFilter: MailTaskFilter;
  onClear: () => void;
};

/** Keeps a task filter visible once the menu closes, so a short list isn't mistaken for all mail. */
const ActiveTaskFilter = ({ taskFilter, onClear }: ActiveTaskFilterProps) => {
  const { label, icon: Icon } = taskFilterOption(taskFilter);

  return (
    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
      <span>Showing</span>
      <span className="inline-flex items-center gap-1 rounded-full border border-kPurple-100 bg-kPurple-50 py-0.5 pl-2 pr-1 font-semibold text-kPurple-500">
        <Icon className="size-3" aria-hidden />
        {label}
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear the ${label} filter`}
          className="flex size-4 items-center justify-center rounded-full hover:bg-kPurple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-3" />
        </button>
      </span>
    </div>
  );
};

export const MailListPane = ({
  items,
  selectedMailId,
  searchText,
  activeSearch,
  view,
  direction,
  taskFilter,
  showCustomers,
  emptyDescription,
  isLoading,
  isLoadingMore,
  hasMore,
  error,
  onSearchTextChange,
  onViewChange,
  onDirectionChange,
  onTaskFilterChange,
  onSelect,
  onNavigate,
  onLoadMore,
  onRetry,
}: MailListPaneProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const groupIdPrefix = useId();
  const groups = useMemo(() => groupByDate(items), [items]);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set());
  const visibleItems = useMemo(
    () => groups.filter(group => !collapsedGroups.has(group.label)).flatMap(group => group.items),
    [groups, collapsedGroups],
  );
  // Scrolling only loads more while the last group is open; a collapsed one would keep the end of
  // the list in view and load every page.
  const isLastGroupOpen = groups.length > 0 && !collapsedGroups.has(groups[groups.length - 1].label);

  const toggleGroup = (label: string) => {
    setCollapsedGroups(previous => {
      const next = new Set(previous);
      if (!next.delete(label)) next.add(label);
      return next;
    });
  };

  // Keeps the selected message in view, and keeps focus on it while moving with the keyboard.
  useEffect(() => {
    if (!selectedMailId || !listRef.current) return;

    const selected = listRef.current.querySelector<HTMLElement>(`[data-mail-id="${selectedMailId}"]`);
    if (!selected) return;

    selected.scrollIntoView({ block: "nearest" });
    if (listRef.current.contains(document.activeElement)) {
      selected.focus({ preventScroll: true });
    }
  }, [selectedMailId, items]);

  // Loads the next page as the end of the list scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || !isLastGroupOpen) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onLoadMore();
      },
      { root: scrollRef.current, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLastGroupOpen, onLoadMore]);

  /** Moves through the emails in open groups; past the last loaded one it loads the next page. */
  const moveSelection = (navigation: MailListNavigation) => {
    if (visibleItems.length === 0) return;

    const index = visibleItems.findIndex(mail => mail.id === selectedMailId);
    const lastIndex = visibleItems.length - 1;

    if (navigation === "next" && index === lastIndex) {
      if (hasMore && visibleItems[lastIndex].id === items[items.length - 1].id) onLoadMore();
      return;
    }

    const target = navigation === "first" ? 0
      : navigation === "last" ? lastIndex
        : index < 0 ? 0
          : navigation === "previous" ? Math.max(index - 1, 0)
            : index + 1;

    onNavigate(visibleItems[target].id);
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      const focusedId = (event.target as HTMLElement).dataset.mailId;
      if (focusedId) {
        event.preventDefault();
        onSelect(focusedId);
      }
      return;
    }

    const navigation = NAVIGATION_KEYS[event.key];
    if (!navigation) return;

    event.preventDefault();
    moveSelection(navigation);
  };

  const showList = !isLoading && items.length > 0;
  const tabStopId = visibleItems.some(mail => mail.id === selectedMailId) ? selectedMailId : visibleItems[0]?.id;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-gray-200 p-3">
        <InputGroup className="bg-white">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            className="[&::-webkit-search-cancel-button]:hidden"
            value={searchText}
            onChange={event => onSearchTextChange(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Escape") onSearchTextChange("");
            }}
            placeholder="Search emails"
            aria-label="Search emails"
          />
          {searchText && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton size="icon-xs" aria-label="Clear search" onClick={() => onSearchTextChange("")}>
                <X />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <div className="mt-2 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <MailDirectionToggle value={direction} onChange={onDirectionChange} />
          </div>
          <MailTaskFilterMenu value={taskFilter} onChange={onTaskFilterChange} />
          <MailListViewToggle value={view} onChange={onViewChange} />
        </div>
        {taskFilter !== "all" && (
          <ActiveTaskFilter taskFilter={taskFilter} onClear={() => onTaskFilterChange("all")} />
        )}
      </div>

      <div ref={scrollRef} className="scrollbar-fluent min-h-0 flex-1 overflow-y-auto">
        {isLoading && <ListSkeleton />}

        {!isLoading && error && items.length === 0 && (
          <div className="space-y-3 p-4 text-center text-sm text-gray-600">
            <p>{error}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw /> Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && items.length === 0 && (
          <ListEmpty
            activeSearch={activeSearch}
            direction={direction}
            taskFilter={taskFilter}
            emptyDescription={emptyDescription}
          />
        )}

        {showList && (
          <div
            ref={listRef}
            role="listbox"
            aria-label="Emails"
            onKeyDown={handleListKeyDown}
          >
            {groups.map((group, groupIndex) => {
              const headingId = `${groupIdPrefix}-group-${groupIndex}`;
              const isCollapsed = collapsedGroups.has(group.label);

              return (
                <ul key={group.label} role="group" aria-labelledby={headingId} className="m-0 list-none p-0">
                  <li role="presentation" className="sticky top-0 z-10 border-y border-kPurple-100 bg-kPurple-50">
                    {/* 16px = the row's 4px left border and 12px padding. Padding, not a border, as page CSS can strip a button's border. */}
                    <button
                      type="button"
                      id={headingId}
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(group.label)}
                      className="m-0 flex w-full items-center gap-1.5 border-0 bg-transparent py-2 pl-4 pr-3 text-left text-sm font-bold uppercase tracking-wide text-kPurple-500 hover:bg-kPurple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      <ChevronRight
                        className={cn("size-3.5 shrink-0 transition-transform", !isCollapsed && "rotate-90")}
                        aria-hidden
                      />
                      {group.label}
                    </button>
                  </li>
                  {!isCollapsed && group.items.map(mail => (
                    <MailListItem
                      key={mail.id}
                      mail={mail}
                      isSelected={mail.id === selectedMailId}
                      isTabStop={mail.id === tabStopId}
                      showCustomers={showCustomers}
                      onSelect={onSelect}
                    />
                  ))}
                </ul>
              );
            })}
          </div>
        )}

        {showList && hasMore && (
          <div ref={sentinelRef} className="p-3">
            <Button variant="outline" size="sm" className="w-full" onClick={onLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? <Spinner /> : <ChevronDown />}
              {isLoadingMore ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}

        {showList && error && (
          <p className="px-3 pb-3 text-center text-xs text-red-700">{error}</p>
        )}
      </div>
    </div>
  );
};
